import React, { FC } from 'react';
import { Formik } from 'formik';
import { Form, FormItem, Input } from 'formik-antd';
import { defaultFormLayout } from 'common/config/formLayout';
import { GenderSelect } from 'common/components/GenderSelect';
import { AppDatePicker } from 'common/components/AppDatePicker';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { PersonalDocumentTypeSelect } from 'common/components/PersonalDocumentTypeSelect';
import { object } from 'yup';
import { FormikOnSubmit } from 'lib/formik.utils';
// import { SaveButton } from 'common/components/SaveButton'; - to uncomment when backend will be ready

import {
  dateOfBirthValidationSchema,
  firstNameValidationSchema,
  genderValidationSchema,
  idNumberValidationSchema,
  lastNameValidationSchema,
  phoneNumberValidationSchema,
  phoneNumberPrefixCodeValidation,
} from 'common/config/validationSchema';
import { ProfileOwnerFormModel } from 'models/user';
import { UserStatus } from 'models/userStatus';

const emptyInitialValues: ProfileOwnerFormModel = {
  partner: '',
  dateOfBirth: '',
  email: '',
  firstName: '',
  gender: '',
  idNumber: '',
  lastName: '',
  phoneNumber: '',
  prefixCode: '',
  id: 0,
  status: UserStatus.Active,
  userId: 0,
};

export interface ProfileOwnerFormProps {
  onSubmit: FormikOnSubmit<ProfileOwnerFormModel>;
  initialValues: ProfileOwnerFormModel;
  loading?: boolean;
}
export const ProfileOwnerForm: FC<ProfileOwnerFormProps> = ({
  onSubmit,
  loading = false,
  initialValues,
}) => {
  const intlMessage = useFormatMessage();

  const validationSchema = object().shape(
    {
      firstName: firstNameValidationSchema(intlMessage),
      lastName: lastNameValidationSchema(intlMessage),
      dateOfBirth: dateOfBirthValidationSchema(intlMessage),
      gender: genderValidationSchema(intlMessage),
      idNumber: idNumberValidationSchema(intlMessage),
      phoneNumber: phoneNumberValidationSchema(intlMessage),
      prefixCode: phoneNumberPrefixCodeValidation(intlMessage),
    },
    [['prefixCode', 'phoneNumber']]
  );

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues || emptyInitialValues}
      onSubmit={onSubmit}
    >
      <Form {...defaultFormLayout}>
        <FormItem
          label={intlMessage({ id: 'person.firstName' })}
          name="firstName"
        >
          <Input name="firstName" disabled />
        </FormItem>

        <FormItem
          label={intlMessage({ id: 'person.lastName' })}
          name="lastName"
        >
          <Input name="lastName" disabled />
        </FormItem>

        <FormItem
          label={intlMessage({ id: 'person.dateOfBirth' })}
          name="dateOfBirth"
        >
          <AppDatePicker disabled name="dateOfBirth" picker="date" />
        </FormItem>

        <FormItem label={intlMessage({ id: 'person.gender' })} name="gender">
          <GenderSelect name="gender" disabled />
        </FormItem>

        <FormItem
          label={intlMessage({ id: 'person.idNumberDocumentType' })}
          name="idNumberDocumentType"
        >
          <PersonalDocumentTypeSelect disabled name="idNumberDocumentType" />
        </FormItem>

        <FormItem
          label={intlMessage({ id: 'person.idNumber' })}
          name="idNumber"
        >
          <Input name="idNumber" disabled />
        </FormItem>

        <FormItem label={intlMessage({ id: 'email' })} name="email">
          <Input name="email" readOnly disabled />
        </FormItem>

        <FormItem
          name="phone"
          label={intlMessage({ id: 'person.phoneNumber' })}
          style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
        >
          <FormItem
            name="phoneNumber"
            style={{ marginBottom: '0', display: 'flex', width: '100%' }}
          >
            <FormItem
              name="prefixCode"
              style={{ marginBottom: '0', width: 95, display: 'inline-block' }}
            >
              <Input
                name="prefixCode"
                style={{ width: 95 }}
                addonBefore={'+'}
                disabled
              />
            </FormItem>
            <Input
              style={{ width: 240, marginLeft: 10 }}
              name="phoneNumber"
              disabled
            />
          </FormItem>
        </FormItem>

        {/* to uncomment when endpoint will PUT will be ready <SaveButton loading={loading} /> */}
      </Form>
    </Formik>
  );
};
